import React, { useState, useEffect } from 'react';
import { useCustomerAuth } from '../../../contexts/CustomerAuthContext';
import { apiFetch } from '../../../utils/api';
import { STEPS } from '../CheckoutModal';

const PurchaseInfoStep = ({ state, dispatch, storeData, storeSlug, formatPrice, goToStep, goBack, cartItems, cartTotal, hasPhysicalProducts = true }) => {
  const { customer, isAuthenticated } = useCustomerAuth();

  const [fullName, setFullName] = useState(state.fullName || '');
  const [whatsappNumber, setWhatsappNumber] = useState(state.whatsappNumber || '');
  const [email, setEmail] = useState(state.email || '');
  const [recipientType, setRecipientType] = useState(state.recipientType || 'myself');
  const [deliveryState, setDeliveryState] = useState(state.deliveryState || '');
  const [deliveryCity, setDeliveryCity] = useState(state.deliveryCity || '');
  const [deliveryAddress, setDeliveryAddress] = useState(state.deliveryAddress || '');
  const [errors, setErrors] = useState({});

  // Pre-fill from customer auth
  useEffect(() => {
    if (isAuthenticated && customer) {
      const fields = {};
      if (!state.fullName && customer.firstName) {
        const name = `${customer.firstName} ${customer.lastName || ''}`.trim();
        fields.fullName = name;
        setFullName(name);
      }
      if (!state.email && customer.email) {
        fields.email = customer.email;
        setEmail(customer.email);
      }
      if (!state.whatsappNumber && customer.phone) {
        fields.whatsappNumber = customer.phone;
        setWhatsappNumber(customer.phone);
      }
      if (Object.keys(fields).length > 0) {
        dispatch({ type: 'SET_FIELDS', payload: fields });
      }
    }
  }, [isAuthenticated, customer]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRecipientChange = (e) => {
    const value = e.target.value;
    setRecipientType(value);
    dispatch({ type: 'UPDATE_FIELD', field: 'recipientType', value });
  };

  const validate = () => {
    const newErrors = {};
    if (!fullName.trim()) newErrors.fullName = 'Full name is required.';
    if (!whatsappNumber.trim()) newErrors.whatsappNumber = 'WhatsApp number is required.';
    if (!email.trim()) {
      newErrors.email = 'Email address is required.';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        newErrors.email = 'Please enter a valid email address.';
      }
    }
    if (hasPhysicalProducts && !deliveryState) newErrors.deliveryState = 'Delivery location is required.';
    if (hasPhysicalProducts && !deliveryAddress.trim()) newErrors.deliveryAddress = 'Delivery address is required.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const fieldValues = {
      fullName: fullName.trim(),
      whatsappNumber: whatsappNumber.trim(),
      email: email.trim(),
      recipientType,
      deliveryState: hasPhysicalProducts ? deliveryState : '',
      deliveryCity: hasPhysicalProducts ? deliveryCity.trim() : '',
      deliveryAddress: hasPhysicalProducts ? deliveryAddress.trim() : '',
      deliveryMethod: hasPhysicalProducts ? state.deliveryMethod : 'digital',
      shippingCalculation: hasPhysicalProducts ? state.shippingCalculation : {
        shippingCost: 0,
        finalShippingCost: 0,
        estimatedDeliveryDays: 0,
        method: 'Digital delivery',
      },
    };

    dispatch({ type: 'SET_FIELDS', payload: fieldValues });

    if (!hasPhysicalProducts) {
      goToStep(STEPS.PAYMENT);
      return;
    }

    // Calculate shipping
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const resp = await apiFetch('/shipping-zones/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          state: deliveryState,
          city: deliveryCity.trim(),
          address: deliveryAddress.trim(),
          orderSubtotal: cartTotal,
          storeSlug
        })
      });
      const data = await resp.json();
      dispatch({ type: 'SET_FIELDS', payload: { shippingCalculation: data } });

      // Merchant has active zones but the customer's state isn't in any of
      // them → block here instead of letting them silently see FREE (from
      // the legacy 3000/50k-threshold fallback) and then get rejected at
      // pay time. Mirror the industry-standard "we don't ship to your
      // location" pattern.
      if (data && data.unserviced === true) {
        const available = Array.isArray(data.availableStates) && data.availableStates.length > 0
          ? `We currently ship to ${data.availableStates.join(', ')}.`
          : 'This store has not configured shipping for your state.';
        setErrors({ deliveryState: `We don't ship to ${data.state || deliveryState} yet. ${available}` });
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to calculate shipping. Please try again.' });
      return;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }

    // Track abandoned checkout in background
    apiFetch('/abandoned-carts/track-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: localStorage.getItem('cartId') || 'anonymous',
        storeSlug,
        items: cartItems.map(item => ({
          productId: item.product.id || item.product._id,
          quantity: item.qty,
          price: item.product.price,
          name: item.product.name
        })),
        subtotal: cartTotal,
        contact: {
          email: email.trim(),
          phone: whatsappNumber.trim(),
          name: fullName.trim()
        }
      })
    }).catch(() => {});

    goToStep(STEPS.CONFIRM_ADDRESS);
  };

  const merchantStates = state.merchantStates || [];

  return (
    <div className="cm-step cm-purchase-info-step">
      {storeData.processingTime && (
        <div className="cm-info-banner">
          <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span>Orders typically ship within {storeData.processingTime}</span>
        </div>
      )}

      <form className="cm-form" onSubmit={handleContinue}>
        <div className="cm-form-group">
          <label className="cm-label" htmlFor="cm-recipient-type">
            Who will receive this order?
          </label>
          <select
            id="cm-recipient-type"
            className="cm-select"
            value={recipientType}
            onChange={handleRecipientChange}
          >
            <option value="myself">Myself</option>
            <option value="someone_else">Someone else</option>
          </select>
        </div>

        <div className="cm-form-group">
          <label className="cm-label" htmlFor="cm-full-name">
            Full Name <span className="cm-required">*</span>
          </label>
          <input
            id="cm-full-name"
            className={`cm-input${errors.fullName ? ' cm-input-error' : ''}`}
            type="text"
            placeholder="John Doe"
            value={fullName}
            onChange={(e) => {
              setFullName(e.target.value);
              if (errors.fullName) setErrors(prev => ({ ...prev, fullName: '' }));
            }}
            required
          />
          {errors.fullName && <p className="cm-form-error">{errors.fullName}</p>}
        </div>

        <div className="cm-form-group">
          <label className="cm-label" htmlFor="cm-whatsapp">
            WhatsApp Number <span className="cm-required">*</span>
          </label>
          <input
            id="cm-whatsapp"
            className={`cm-input${errors.whatsappNumber ? ' cm-input-error' : ''}`}
            type="tel"
            placeholder="+234 800 000 0000"
            value={whatsappNumber}
            onChange={(e) => {
              setWhatsappNumber(e.target.value);
              if (errors.whatsappNumber) setErrors(prev => ({ ...prev, whatsappNumber: '' }));
            }}
            required
          />
          {errors.whatsappNumber && <p className="cm-form-error">{errors.whatsappNumber}</p>}
        </div>

        <div className="cm-form-group">
          <label className="cm-label" htmlFor="cm-email">
            Email Address <span className="cm-required">*</span>
          </label>
          <input
            id="cm-email"
            className={`cm-input${errors.email ? ' cm-input-error' : ''}`}
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
            }}
            required
          />
          {errors.email && <p className="cm-form-error">{errors.email}</p>}
        </div>

        {hasPhysicalProducts ? (
          <>
            <div className="cm-form-group">
              <label className="cm-label" htmlFor="cm-delivery-state">
                Delivery Location <span className="cm-required">*</span>
              </label>
              {merchantStates.length > 0 ? (
                <select
                  id="cm-delivery-state"
                  className={`cm-select${errors.deliveryState ? ' cm-input-error' : ''}`}
                  value={deliveryState}
                  onChange={(e) => {
                    setDeliveryState(e.target.value);
                    if (errors.deliveryState) setErrors(prev => ({ ...prev, deliveryState: '' }));
                  }}
                  required
                >
                  <option value="">Select delivery location</option>
                  {merchantStates.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              ) : (
                <p className="cm-form-hint">No delivery locations available</p>
              )}
              {errors.deliveryState && <p className="cm-form-error">{errors.deliveryState}</p>}
            </div>

            <div className="cm-form-group">
              <label className="cm-label" htmlFor="cm-delivery-city">
                City
              </label>
              <input
                id="cm-delivery-city"
                className="cm-input"
                type="text"
                placeholder="e.g. Ikeja, Lekki, Wuse"
                value={deliveryCity}
                onChange={(e) => setDeliveryCity(e.target.value)}
              />
            </div>

            <div className="cm-form-group">
              <label className="cm-label" htmlFor="cm-delivery-address">
                Delivery Address <span className="cm-required">*</span>
              </label>
              <textarea
                id="cm-delivery-address"
                className={`cm-textarea${errors.deliveryAddress ? ' cm-input-error' : ''}`}
                placeholder="Enter your full delivery address"
                value={deliveryAddress}
                onChange={(e) => {
                  setDeliveryAddress(e.target.value);
                  if (errors.deliveryAddress) setErrors(prev => ({ ...prev, deliveryAddress: '' }));
                }}
                rows={3}
                required
              />
              {errors.deliveryAddress && <p className="cm-form-error">{errors.deliveryAddress}</p>}
            </div>
          </>
        ) : (
          <div className="cm-info-banner">
            <span>Digital products are delivered online. No delivery fee or shipping address is required.</span>
          </div>
        )}

        <div className="cm-step-footer">
          <button type="button" className="cm-btn cm-btn-secondary" onClick={goBack}>
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>
          <button type="submit" className="cm-btn cm-btn-primary" disabled={state.loading}>
            {state.loading ? 'Loading...' : 'Continue'}
            {!state.loading && (
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PurchaseInfoStep;
